/*{ pending } @module Actions.Event
 *
 */
import { Dispatch } from "react";
import { ActionType, action } from "typesafe-actions";
import { DateTime } from "luxon";
import { cloneDeep } from "lodash";
import { RootState } from "typedefs";

import {
  schedulingFetchGet,
  schedulingFetchDelete,
  schedulingFetchPost,
  schedulingFetchPatch,
} from "app/utils/request/scheduling/fetch";

import {
  formatFullDateTime,
  formatFullWeekdayDateTime,
} from "app/utils/luxonFormats";
import {
  EventModel,
  EventResponse,
  EventType,
  LessonSchedule,
  CoachScheduleBlock,
  TimeSlotReservation,
} from "app/models/EventModel";

import { serializeObject } from "app/utils/request/model";

import { toast } from "react-toastify";
import i18n from "app/i18n";
import { CoachProfileModel } from "app/models";
import { DEFAULT_CALENDAR_PREFERENCES } from "app/models/CalendarPreferences";
import {
  ApiValidationError,
  SchedulingErrorResponse,
  SchedulingValidationError,
} from "app/models/scheduling/SchedulingErrorResponse";

const fetchPending = () => action("@events.fetch.pending");

const fetchError = (err: string) => action("@events.fetch.error", err);

const fetchSuccess = (events: EventModel[]) =>
  action("@events.fetch.success", events);

const appendSuccess = (events: EventModel[]) =>
  action("@events.fetch.append", events);

const mergeSuccess = (events: EventModel[], start: string, end: string) =>
  action("@events.fetch.merge", { events, start, end });

export const removeEvents = (ids: number[], eventType: EventType) =>
  action("@events.remove", { ids, eventType });
const selectEvent = (event: EventModel) => action("@events.select", event);
export const deselectEvent = () => action("@events.deselect");
export const clearNewEvent = (event: EventModel) =>
  action("@events.new.clear", event);
export const clearEvents = () => action("@events.clear");

const createNewEvent = (event: EventModel) => action("@events.new.init", event);
const createPending = () => action("@events.create.pending");
const createPendingDone = () => action("@events.create.pendingDone");
const createSuccess = (events: EventModel[]) =>
  action("@events.create.success", events);
const createError = (errors?: any) => action("@events.create.error", errors);
const createValidationError = (errors?: SchedulingValidationError) =>
  action("@events.create.validationError", errors);

export const startEdit = () => action("@events.edit.start");
export const endEdit = () => action("@events.edit.end");
const editSuccess = (
  events: EventModel[],
  type: EventType,
  recurrenceGroupId: number = null,
) => {
  return action("@events.edit.success", { events, type, recurrenceGroupId });
};

const editPending = () => action("@events.edit.pending");
const editPendingDone = () => action("@events.edit.pendingDone");
const editError = (errors?: any) => action("@events.edit.error", errors);
const editValidationError = (errors?: any) =>
  action("@events.edit.validationError", errors);

export const requireConfirmation = (content: any) =>
  action("@events.requestConfirmation", content);
export const dismissConfirmation = () => action("@events.dismissConfirmation");

const showInfoPopup = (content: any) =>
  action("@events.showInfoPopup", content);

// A bay conflict arrives with `can_override: false`. A coach can book over a
// coach conflict, and not over a bay that another lesson holds.
export const conflictCanOverride = (err: any): boolean =>
  err?.errors?.[0]?.details?.[0]?.can_override !== false;

// A conflict that the user can override asks for a confirmation. A bay
// conflict has no override, so its popup says that the time is taken.
export const conflictTitle = (err: any): string =>
  conflictCanOverride(err)
    ? i18n.t("Please confirm that you would like to proceed")
    : i18n.t("This time is not available");

// A 422 on the bay goes under the resource field of the form. Every other 422
// on a lesson save keeps the payment popup.
export const isResourceValidationError = (err: SchedulingErrorResponse): boolean =>
  (err?.errors ?? []).some(
    (e) => e?.source?.parameter === "schedule_resource_id",
  );
export const hideInfoPopup = () => action("@events.hideInfoPopup");

const updateEventPaymentStatus = (id: number, payment_status: string) =>
  action("@events.updatePaymentStatus", { id, payment_status });

export const thisActions = {
  fetchPending,
  fetchError,
  fetchSuccess,
  appendSuccess,
  mergeSuccess,
  selectEvent,
  deselectEvent,
  removeEvents,
  requireConfirmation,
  dismissConfirmation,
  createNewEvent,
  createSuccess,
  createError,
  createValidationError,
  clearNewEvent,
  createPending,
  createPendingDone,
  startEdit,
  endEdit,
  editPending,
  editPendingDone,
  showInfoPopup,
  hideInfoPopup,
  updateEventPaymentStatus,
  editSuccess,
  editError,
  editValidationError,
  clearEvents,
};

export type EventAction = ActionType<typeof thisActions>;

function parseError(errors: ApiValidationError[]): SchedulingValidationError {
  return errors.reduce<SchedulingValidationError>(
    (
      accum: SchedulingValidationError,
      err: ApiValidationError,
    ): SchedulingValidationError => {
      if (!accum[err.source.parameter]) {
        accum[err.source.parameter] = [];
      }
      accum[err.source.parameter] = err.details.map((d) => d.message);

      return accum;
    },
    {},
  );
}

// Pull a human-readable message out of a 422 payment failure. Prefer the
// payment-method-specific error (e.g. "Athlete has no saved card on file")
// when present, then any other validation detail, then the legacy `error`
// scalar, and finally a generic fallback.
function paymentErrorMessage(err: SchedulingErrorResponse): string {
  const validationErrors = err.errors ?? [];
  const paymentMethodError = validationErrors.find(
    (e) => e?.source?.parameter === "payment_method",
  );
  const firstDetail = (paymentMethodError ?? validationErrors[0])
    ?.details?.[0]?.message;

  return (
    firstDetail ||
    err.error ||
    i18n.t("Payment is currently being processed. Please try again shortly.")
  );
}

const parseEvents = (
  events: EventResponse[],
): (LessonSchedule | CoachScheduleBlock | TimeSlotReservation)[] => {
  return events
    .filter((ev) => Object.values(EventType).includes(ev.type))
    .map((event) => {
      if (event.type === "coach_schedule_block" || event.type === "program") {
        return EventModel.buildInstance(event);
      }

      return EventModel.buildInstance({
        ...event,
      });
    });
};

export function createEvent(event: EventModel) {
  if (event.type === "lesson_schedule") {
    return createSession(event);
  } else {
    return createScheduleBlock(event);
  }
}

export function editEvent(event: EventModel) {
  if (event.type === "lesson_schedule") {
    return editSession(event);
  } else {
    return editScheduleBlock(event);
  }
}

function createSession(event: EventModel) {
  return (dispatch: Dispatch<EventAction>, getState: () => RootState) => {
    dispatch(createPending());
    schedulingFetchPost<EventResponse[]>("lesson_schedules", {
      ...event,
      source: "web",
    })
      .then((events: EventResponse[]) => {
        const sessions = parseEvents(events);

        const firstSession = sessions[0];
        const preferences =
          getState().calendarPreferences.data ?? DEFAULT_CALENDAR_PREFERENCES;
        const formattedStart = formatFullWeekdayDateTime(
          DateTime.fromISO(firstSession.start).setZone(firstSession.timeZone),
          preferences.dateFormat,
          preferences.timeFormat,
        );
        dispatch(createSuccess(sessions));
        dispatch(
          showInfoPopup({
            title: i18n.t("New Session Created"),
            content: i18n.t("You created a new session on"),
            subcontent: `${formattedStart}.`,
            item: firstSession,
            status: "success",
          }),
        );
      })
      .catch((err: SchedulingErrorResponse) => {
        if (err.status === 500) {
          dispatch(
            showInfoPopup({
              title: i18n.t("Something went wrong"),
              content: i18n.t(
                "A new session wasn't created, please try again.",
              ),
              status: "failed",
            }),
          );
          dispatch(createError());
        } else if (err.status === 422 && !isResourceValidationError(err)) {
          dispatch(
            showInfoPopup({
              title: i18n.t("Payment Processing"),
              content: paymentErrorMessage(err),
              status: "failed",
            }),
          );
          dispatch(createError());
        } else if (err.status === 409) {
          dispatch(
            showInfoPopup({
              title: conflictTitle(err),
              content: err.errors[0].details[0].message,
              status: "conflict",
              canOverride: conflictCanOverride(err),
              payload: event,
              action: "create",
            }),
          );

          dispatch(createPendingDone());
        } else {
          const validationErrors = serializeObject<SchedulingValidationError>(
            parseError(err.errors),
          );
          dispatch(createValidationError(validationErrors));
        }
      });
  };
}

function createScheduleBlock(event: EventModel) {
  return (dispatch: Dispatch<EventAction>, getState: () => RootState) => {
    dispatch(createPending());
    return schedulingFetchPost<EventResponse[]>("coach_schedule_blocks", {
      ...event,
      source: "web",
    })
      .then((events: EventResponse[]) => {
        const blocks = parseEvents(events);
        const firstBlock = blocks[0];
        const preferences =
          getState().calendarPreferences.data ?? DEFAULT_CALENDAR_PREFERENCES;
        const formattedStart = formatFullWeekdayDateTime(
          DateTime.fromISO(firstBlock.start).setZone(firstBlock.timeZone),
          preferences.dateFormat,
          preferences.timeFormat,
        );

        dispatch(createSuccess(blocks));
        dispatch(
          showInfoPopup({
            title: i18n.t("New Unavailability Block Created"),
            content: i18n.t("You created a new unavailability block on"),
            subcontent: `${formattedStart}.`,
            status: "success",
            item: firstBlock,
          }),
        );
      })
      .catch((err) => {
        if (err.status === 500) {
          dispatch(
            showInfoPopup({
              title: i18n.t("Something went wrong"),
              content: i18n.t(
                "The unavailability block wasn't created, please try again.",
              ),
              status: "failed",
            }),
          );
          dispatch(createError());
        } else if (err.status === 409) {
          dispatch(
            showInfoPopup({
              title: conflictTitle(err),
              content: err.errors[0].details[0].message,
              status: "conflict",
              canOverride: conflictCanOverride(err),
              payload: event,
              action: "create",
            }),
          );
          dispatch(createPendingDone());
        } else {
          const validationErrors = serializeObject<SchedulingValidationError>(
            parseError(err.errors),
          );
          dispatch(createValidationError(validationErrors));
        }
      });
  };
}

function editSession(payload: EventModel) {
  return (dispatch: Dispatch<EventAction>, getState: () => RootState) => {
    dispatch(editPending());

    return schedulingFetchPatch<EventResponse[]>(
      `lesson_schedules/${payload.id}`,
      payload,
    )
      .then((events: EventResponse[]) => {
        const sessions = parseEvents(events);
        const firstSession = sessions[0];
        const preferences =
          getState().calendarPreferences.data ?? DEFAULT_CALENDAR_PREFERENCES;
        const formattedStart = formatFullDateTime(
          DateTime.fromISO(firstSession.start).setZone(firstSession.timeZone),
          preferences.dateFormat,
          preferences.timeFormat,
        );
        dispatch(
          editSuccess(sessions, payload.type, payload.recurrenceGroupId),
        );
        dispatch(
          showInfoPopup({
            title: i18n.t("Session Updated"),
            content: i18n.t("This session is scheduled on"),
            subcontent: `${formattedStart}.`,
            status: "success",
            item: firstSession,
          }),
        );
      })
      .catch((err) => {
        if (err.status === 500) {
          dispatch(
            showInfoPopup({
              title: i18n.t("Something went wrong"),
              content: i18n.t(
                "The unavailability block wasn't created, please try again.",
              ),
              status: "failed",
            }),
          );
          dispatch(editError());
        } else if (err.status === 422 && !isResourceValidationError(err)) {
          dispatch(
            showInfoPopup({
              title: i18n.t("Payment Processing"),
              content: paymentErrorMessage(err),
              status: "failed",
            }),
          );
          dispatch(editError());
        } else if (err.status === 409) {
          dispatch(
            showInfoPopup({
              title: conflictTitle(err),
              content: err.errors[0].details[0].message,
              status: "conflict",
              canOverride: conflictCanOverride(err),
              action: "edit",
              payload,
            }),
          );

          dispatch(editPendingDone());
        } else {
          const validationErrors = serializeObject<SchedulingValidationError>(
            parseError(err.errors),
          );
          dispatch(editValidationError(validationErrors));
        }
      });
  };
}

function editScheduleBlock(payload: EventModel) {
  return (dispatch: Dispatch<EventAction>, getState: () => RootState) => {
    dispatch(editPending());
    return schedulingFetchPatch<EventResponse[]>(
      `coach_schedule_blocks/${payload.id}`,
      payload,
    )
      .then((events: EventResponse[]) => {
        const sessions = parseEvents(events);
        const firstSession = sessions[0];
        const preferences =
          getState().calendarPreferences.data ?? DEFAULT_CALENDAR_PREFERENCES;
        const formattedStart = formatFullDateTime(
          DateTime.fromISO(firstSession.start).setZone(firstSession.timeZone),
          preferences.dateFormat,
          preferences.timeFormat,
        );
        dispatch(
          editSuccess(sessions, payload.type, payload.recurrenceGroupId),
        );
        dispatch(
          showInfoPopup({
            title: i18n.t("Unavailability Block Updated"),
            content: i18n.t("This block is scheduled on"),
            subcontent: `${formattedStart}.`,
            status: "success",
            item: firstSession,
          }),
        );
      })
      .catch((err) => {
        if (err.status === 500) {
          dispatch(
            showInfoPopup({
              title: i18n.t("Something went wrong"),
              content: i18n.t(
                "The unavailability block wasn't updated, please try again.",
              ),
              status: "failed",
            }),
          );
          dispatch(editError());
        } else if (err.status === 409) {
          dispatch(
            showInfoPopup({
              title: conflictTitle(err),
              content: err.errors[0].details[0].message,
              status: "conflict",
              canOverride: conflictCanOverride(err),
              action: "edit",
              payload,
            }),
          );
          dispatch(editPendingDone());
        } else {
          const validationErrors = serializeObject<SchedulingValidationError>(
            parseError(err.errors),
          );
          dispatch(editValidationError(validationErrors));
        }
      });
  };
}

type NewEventInit = {
  start: string;
  end: string;
  date: DateTime;
  type: EventType;
  timeZone: string;
  backgroundColor: string;
  repeat?: null;
  lessonRateId?: number | null;
  coachProfiles?: CoachProfileModel[];
  rate?: number;
  location?: string;
  scheduleResourceId?: number | null;
  // The bay column of the click. The block form starts on this bay.
  clickedBayId?: number | null;
};

export function initializeCopyEvent(event: EventModel) {
  return (dispatch: Dispatch<EventAction>) => {
    const isCheckout = event.online_payment_type === 'checkout';

    const copiedEvent = {
      ...event,
      id: undefined,
      recurrenceGroupId: undefined,
      payment_editable: undefined,
      feedPostId: undefined,
      scheduleProviderDisplayName: undefined,
      externalCalendarId: undefined,
      ...(isCheckout
        ? {
            online_payment_type: undefined,
            charge_timing: undefined,
            payment_method: undefined,
            payment_status: undefined,
          }
        : {
            preservePaymentDefaults: true,
          }
      ),
    };
    dispatch(createNewEvent(copiedEvent as EventModel));
  };
}

export function initializeNewEvent({
  start,
  end,
  type,
  timeZone,
  date,
  lessonRateId,
  rate,
  coachProfiles = [],
  location = "",
  scheduleResourceId = null,
  clickedBayId = null,
}: NewEventInit) {
  return (dispatch: Dispatch<EventAction>) => {
    const newEvent = {
      start,
      end,
      type,
      date,
      timeZone,
      repeat: null,
      studentProfiles: [],
      lessonRateId,
      rate,
      location,
      coachProfiles,
      scheduleResourceId,
      clickedBayId,
    };
    // @ts-expect-error newEvent is a partial EventModel and the cast does not cover every field.
    dispatch(createNewEvent(newEvent as EventModel));
  };
}

export function withConfirmation({ content }: { content: string }) {
  return (dispatch: Dispatch<EventAction>) => {
    dispatch(
      requireConfirmation({
        content,
      }),
    );
  };
}

export function deleteEvent(
  event: EventModel,
  applyToRecurrence?: string,
  chargeLate?: boolean,
  notifyStudents = true,
) {
  if (event.type === "lesson_schedule" || !event.type) {
    return deleteLessonSchedule(event, applyToRecurrence, chargeLate, notifyStudents);
  } else {
    return deleteScheduleBlock(event, applyToRecurrence);
  }
}

function buildDeletePayload(
  event: EventModel,
  recurrence?: string,
  chargeLate?: boolean,
  notifyStudents = true,
): Record<string, any> {
  // CN-2798: always a real boolean; the backend treats a missing value as true.
  const payload: Record<string, any> = { applyToRecurrence: recurrence, notifyStudents };

  // Check if session is non-refundable first
  const isNonRefundable = event.refundable === false;

  if (isNonRefundable) {
    // Non-refundable sessions always send refund: false
    payload.refund = false;
    return payload;
  }

  // Existing refund logic for refundable sessions
  const isOnline = event.payment_method === "online";
  const isPackage = event.payment_method === "package";
  const rate = Number(event?.rate ?? 0);

  if ((isOnline && rate > 0) || isPackage) {
    payload.refund = !chargeLate;
  }

  return payload;
}

function showSuccessPopup(
  dispatch: Dispatch<EventAction>,
  recurrence?: string,
) {
  // For calendar (coach view), just show simple cancellation message
  // No need to mention refunds or contacting coach since they ARE the coach
  const title = i18n.t(
    recurrence === "following" ? "Sessions Canceled" : "Session Canceled",
  );
  const content = i18n.t(
    recurrence === "following"
      ? "This and all following sessions have been successfully canceled."
      : "This session has been successfully canceled.",
  );

  dispatch(
    showInfoPopup({
      title,
      content,
      status: "success",
    }),
  );
}

function showErrorPopup(dispatch: Dispatch<EventAction>) {
  dispatch(
    showInfoPopup({
      title: i18n.t("Something went wrong"),
      content: i18n.t(
        "These sessions have not been canceled. Please try again.",
      ),
      status: "error",
    }),
  );
}

function doDelete(
  event: EventModel,
  recurrence: string | undefined,
  dispatch: Dispatch<EventAction>,
  getState: () => RootState,
  chargeLate?: boolean,
  notifyStudents = true,
) {
  return schedulingFetchDelete(
    `lesson_schedules/${event.id}`,
    buildDeletePayload(event, recurrence, chargeLate, notifyStudents),
  )
    .then(() => {
      const state = getState();
      const followingIds =
        recurrence === "following"
          ? getFollowingEvents(state.events.data, event).map((ev) => ev.id)
          : [];

      dispatch(
        removeEvents([event.id, ...followingIds], EventType.LESSON_SCHEDULE),
      );
      showSuccessPopup(dispatch, recurrence);
    })
    .catch((err) => {
      if (err?.status === 422) {
        dispatch(
          showInfoPopup({
            title: i18n.t("Payment Processing"),
            content: err.error || i18n.t("Payment is currently being processed. Please try again shortly."),
            status: "failed",
          }),
        );
      } else {
        console.error("fetchErr", err);
        showErrorPopup(dispatch);
      }
    });
}

export function deleteLessonSchedule(
  event: EventModel,
  applyToRecurrence?: string,
  chargeLate?: boolean,
  notifyStudents = true,
) {
  return (dispatch: Dispatch<EventAction>, getState: () => RootState) => {
    return doDelete(event, applyToRecurrence, dispatch, getState, chargeLate, notifyStudents);
  };
}

function deleteScheduleBlock(event: EventModel, applyToRecurrence?: string) {
  return (dispatch: Dispatch<EventAction>, getState: () => RootState) => {
    return schedulingFetchDelete(`coach_schedule_blocks/${event.id}`, {
      applyToRecurrence,
    })
      .then(() => {
        if (applyToRecurrence === "following") {
          const followingEventIds = getFollowingEvents(
            getState().events.data,
            event,
          ).map((ev) => ev.id);
          dispatch(
            removeEvents(
              [event.id, ...followingEventIds],
              EventType.COACH_SCHEDULE_BLOCK,
            ),
          );
        } else {
          dispatch(removeEvents([event.id], EventType.COACH_SCHEDULE_BLOCK));
        }

        dispatch(
          showInfoPopup({
            title: i18n.t("Unavailability Block Canceled"),
            content: i18n.t(
              "This unavailability block has been successfully canceled.",
            ),
            status: "success",
          }),
        );
      })
      .catch((err) => {
        console.error("fetchErr", err);

        dispatch(
          showInfoPopup({
            title: i18n.t("Something went wrong"),
            content: i18n.t(
              "This unavailability block has not been canceled. Please try again.",
            ),
            status: "error",
          }),
        );
      });
  };
}

export function setSelectedEvent(id: number) {
  return (dispatch: Dispatch<EventAction>, getState: () => RootState) => {
    const events: EventModel[] = getState().events.data;
    const selected = cloneDeep(events.find((event) => event.id === id));
    dispatch(selectEvent(selected));
  };
}

let fetchEventsController: AbortController | null = null;

export function fetchEvents(params?: { [key: string]: any }) {
  return (dispatch: Dispatch<EventAction>) => {
    if (fetchEventsController) {
      fetchEventsController.abort();
    }
    fetchEventsController = new AbortController();
    const signal = fetchEventsController.signal;

    dispatch(fetchPending());
    return schedulingFetchGet<EventResponse[]>(
      "events",
      params || {},
      {},
      signal,
    )
      .then((events: EventResponse[]) => {
        const models = parseEvents(events);
        dispatch(fetchSuccess(models));
      })
      .catch((err) => {
        if (err && err.name === "AbortError") {
          return;
        }
        console.error("fetchErr", err);
        dispatch(fetchError(err));
      });
  };
}

let fetchMoreEventsController: AbortController | null = null;

export function fetchMoreEvents(params?: { [key: string]: any }) {
  return (dispatch: Dispatch<EventAction>) => {
    if (fetchMoreEventsController) {
      fetchMoreEventsController.abort();
    }
    fetchMoreEventsController = new AbortController();
    const signal = fetchMoreEventsController.signal;

    return schedulingFetchGet<EventResponse[]>(
      "events",
      params || {},
      {},
      signal,
    )
      .then((events: EventResponse[]) => {
        const models = parseEvents(events);
        dispatch(appendSuccess(models));
      })
      .catch((err) => {
        if (err && err.name === "AbortError") {
          return;
        }
        console.error("fetchMoreErr", err);
        dispatch(fetchError(err));
      });
  };
}

let pollController: AbortController | null = null;

export function pollSegments(segments: Array<{ start: string; end: string }>) {
  return (dispatch: Dispatch<EventAction>) => {
    if (pollController) {
      pollController.abort();
    }
    pollController = new AbortController();
    const signal = pollController.signal;

    const promises = segments.map(({ start, end }) =>
      schedulingFetchGet<EventResponse[]>("events", { start, end }, {}, signal)
        .then((events: EventResponse[]) => {
          const models = parseEvents(events);
          dispatch(mergeSuccess(models, start, end));
        })
        .catch((err) => {
          if (err && err.name === "AbortError") {
            return;
          }
          console.error("pollErr", err);
        }),
    );

    return Promise.all(promises);
  };
}

let fetchScheduleController: AbortController | null = null;

export function fetchSchedule(params?: { after?: string; limit?: number }) {
  return (dispatch: Dispatch<EventAction>) => {
    if (fetchScheduleController) {
      fetchScheduleController.abort();
    }
    fetchScheduleController = new AbortController();
    const signal = fetchScheduleController.signal;

    dispatch(fetchPending());
    return schedulingFetchGet<EventResponse[]>(
      "schedule",
      params || {},
      {},
      signal,
    )
      .then((events: EventResponse[]) => {
        const models = parseEvents(events);
        dispatch(fetchSuccess(models));
      })
      .catch((err) => {
        if (err && err.name === "AbortError") {
          return;
        }
        console.error("fetchScheduleErr", err);
        dispatch(fetchError(err));
      });
  };
}

let fetchMoreScheduleController: AbortController | null = null;

export function fetchMoreSchedule(params?: { after?: string; limit?: number }) {
  return (dispatch: Dispatch<EventAction>) => {
    if (fetchMoreScheduleController) {
      fetchMoreScheduleController.abort();
    }
    fetchMoreScheduleController = new AbortController();
    const signal = fetchMoreScheduleController.signal;

    return schedulingFetchGet<EventResponse[]>(
      "schedule",
      params || {},
      {},
      signal,
    )
      .then((events: EventResponse[]) => {
        const models = parseEvents(events);
        dispatch(appendSuccess(models));
        return models.length;
      })
      .catch((err) => {
        if (err && err.name === "AbortError") {
          return 0;
        }
        console.error("fetchMoreScheduleErr", err);
        dispatch(fetchError(err));
        return 0;
      });
  };
}

let pollScheduleController: AbortController | null = null;

export function pollScheduleSegments(
  segments: Array<{ after: string; limit: number; mergeEnd: string }>,
) {
  return (dispatch: Dispatch<EventAction>) => {
    if (pollScheduleController) {
      pollScheduleController.abort();
    }
    pollScheduleController = new AbortController();
    const signal = pollScheduleController.signal;

    const promises = segments.map(({ after, limit, mergeEnd }) =>
      schedulingFetchGet<EventResponse[]>(
        "schedule",
        { after, limit },
        {},
        signal,
      )
        .then((events: EventResponse[]) => {
          const models = parseEvents(events).filter((m) => m.start < mergeEnd);
          dispatch(mergeSuccess(models, after, mergeEnd));
        })
        .catch((err) => {
          if (err && err.name === "AbortError") {
            return;
          }

          console.error("pollScheduleErr", err);
        }),
    );

    return Promise.all(promises);
  };
}

let fetchFacilityEventsController: AbortController | null = null;

// Only the facility calendar calls this action. Every call asks for the blocks
// of the bays too. The service sends them only while the facility has the
// feature on. A client without the param (an old mobile build) gets no block
// without a coach. The Coaches view has no bay column, so the calendar does
// not show a bay block there.
export function fetchFacilityEvents(params?: { [key: string]: any }) {
  return (dispatch: Dispatch<EventAction>) => {
    if (fetchFacilityEventsController) {
      fetchFacilityEventsController.abort();
    }
    fetchFacilityEventsController = new AbortController();
    const signal = fetchFacilityEventsController.signal;

    dispatch(fetchPending());
    return schedulingFetchGet<EventResponse[]>(
      "facilities/events",
      { ...params, include_resource_blocks: 1 },
      {},
      signal,
    )
      .then((events: EventResponse[]) => {
        const models = parseEvents(events);
        dispatch(fetchSuccess(models));
      })
      .catch((err) => {
        if (err && err.name === "AbortError") {
          return;
        }
        console.error("fetchErr", err);
        dispatch(fetchError(err));
      });
  };
}

function getFollowingEvents(events: EventModel[], event: EventModel) {
  return events.filter((ev) => {
    return (
      ev.type === event.type &&
      ev.recurrenceGroupId === event.recurrenceGroupId &&
      ev.interval.isAfter(event.interval.start)
    );
  });
}

export function togglePaymentStatus(event: EventModel) {
  return (dispatch: Dispatch<EventAction>) => {
    const newStatus = event.payment_status === "paid" ? "pending" : "paid";

    schedulingFetchPatch(`lesson_schedules/${event.id}`, {
      payment_status: newStatus,
    })
      .then(() => {
        dispatch(updateEventPaymentStatus(event.id, newStatus));
        toast.success(
          newStatus === "paid"
            ? i18n.t("Session marked as paid")
            : i18n.t("Session marked as unpaid"),
        );
      })
      .catch(() => {
        toast.error(i18n.t("Payment status could not be updated."));
      });
  };
}
